import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {RESTService} from "../../services/rest.service";
import {StorageService} from "../../services/storage.service";
import {GlobalStorageService} from "../../services/global-storage.service";
import {UtilsService} from "../../services/utils.service";
import {IndexedDB} from "../../services/indexed-db.service";

@Component({
  selector: 'app-report',
  templateUrl: './report.component.html',
  styleUrls: ['./report.component.scss'],
  standalone: false
})

export class ReportComponent implements OnChanges {
  @Input() isFollowUpNotesVisible: boolean = false;
  @Input() isReportDetailsVisible: boolean = false;
  @Input() isFollowUpControlsBlockVisible: boolean = false;
  @Input() reportType: string = '';
  @Input() reportData: any = {};
  @Input() isPreviousVisit: boolean = false;

  @Output() statusChange = new EventEmitter<boolean>();

  reportGroups: any[] = [
    {title: 'Flow', items: []},
    {title: 'Pictures and Notes', items: []},
    {title: 'Trainings', items: []},
    {title: 'Services', items: []},
    {title: 'Education Module', items: []},
    {title: 'Signature', items: []}
  ];

  followUpGroups: any[] = [
    {title: 'Flow', items: []},
    {title: 'Pictures and Notes', items: []},
    {title: 'Site Inventory', items: []},
    {title: 'Trainings', items: []},
    {title: 'Services', items: []},
    {title: 'Education Module', items: []}
  ];

  report: any = {};
  followUpNotes: any = {};
  isDataExist: boolean = true;

  dataExistTimeout: any = null;
  isShowImageShareButton: boolean = false;

  reportStatus: boolean = false;
  followUpStatus: boolean = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private rest: RESTService,
    private storage: StorageService,
    public global: GlobalStorageService,
    private utils: UtilsService,
    private db: IndexedDB) {
  }

  async getImage(note: any) {
    if (this.global.get('isOffline')) {
      return note.filename ? await this.utils.readFile(note.filename) : (note.photoLink ? this.utils.getDefaultImage() : '');
    } else {
      return note.filename ? await this.utils.readFile(note.filename) : (note.photoLink ? note.photoLink : '');
    }
  }

  onStatusChange() {
    this.statusChange.emit(this.reportStatus && this.followUpStatus);
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes && changes['reportData'] && changes['reportData'].currentValue && changes['reportData'].currentValue.id) {
      let data: any = Object.assign({}, changes['reportData'].currentValue);
      this.report = Object.assign({}, data);
      this.followUpNotes = await this.db.readItem('visits', data.id);
      const trainings = [];
      for (const item of this.report.trainingCategories) {
        const comments = [];
        for (const comment of item.comments) {
          if (comment.isTrue) {
            if (!comment.notes?.length && comment.notesIds?.length) {
              let notes: any = await this.db.readItemsByIds('notes', comment.notesIds);
              comment.notes = this.utils.modifyNotes(notes) || [];
            }
            comment.notes = comment.notes.filter((item: any) => {
              return item.typeOfNote === "Report Note"
            });
            for (let n of comment.notes) {
              n.img = await this.getImage(n);
            }
            comments.push(comment);
          }
        }
        if (comments.length) {
          trainings.push({category: item.name, comments});
        }
      }

      /* Services */
      const services = [];
      for (const item of this.report.serviceCategories) {
        const comments = [];
        for (const comment of item.comments) {
          if (comment.isTrue) {
            if (!comment.notes?.length && comment.notesIds?.length) {
              let notes: any = await this.db.readItemsByIds('notes', comment.notesIds);
              comment.notes = this.utils.modifyNotes(notes) || [];
            }
            comment.notes = comment.notes.filter((item: any) => {
              return item.typeOfNote === "Report Note"
            });
            for (let n of comment.notes) {
              n.img = await this.getImage(n);
            }
            comments.push(comment);
          }
        }
        if (comments.length) {
          services.push({category: item.name, comments});
        }
      }

      /* Education Modules */
      const educationModules = [];
      for (const item of this.report.educationModulesCategories) {
        const comments = [];
        if (item.comments?.length) {
          for (const comment of item.comments) {
            if (comment.isTrue) {
              if (!comment.notes?.length && comment.notesIds?.length) {
                let notes: any = await this.db.readItemsByIds('notes', comment.notesIds);
                comment.notes = this.utils.modifyNotes(notes) || [];
              }
              comment.notes = comment.notes.filter((item: any) => {
                return item.typeOfNote === "Report Note"
              });
              for (let n of comment.notes) {
                n.img = await this.getImage(n);
              }
              comments.push(comment);
            }
          }
        }

        if (comments.length) {
          educationModules.push({category: item.name, comments});
        }
      }

      let picturesAndNotes: any = await this.db.readItemsByIds('notes', data.picturesAndNotes);
      picturesAndNotes = this.utils.modifyNotes(picturesAndNotes);
      picturesAndNotes = picturesAndNotes.filter((item: any) => {
        return item.typeOfNote === "Report Note"
      });

      for (let n of picturesAndNotes) {
        n.img = await this.getImage(n);
      }

      let signature: any = "";
      if (data.signature) {
        if (data.signature.includes("https")) {
          signature = this.global.get('isOffline') ? this.utils.getDefaultImage() : data.signature;
        } else {
          signature = await this.utils.readFile(data.signature);
        }
      }

      let flow: any = data.questions.filter((item: any) => item.answer);
      for (let f of flow) {
        f.isVisible = f['answer'] === 'N/C';

        if (!f.note?._id && f['noteID']) {
          f.note = await this.db.readItem('notes', f['noteID']);
          f.note = this.utils.modifyNote(f.note);
          if (f.note.typeOfNote === 'Report Note') {
            f.isVisible = true;
          } else {
            delete f.note;
            f.noteID = "";
          }
        }

        if (f['additionalNotesIds']?.length && !f.additionalNotes.length) {
          const additionalNotes: any = await this.db.readItemsByIds('notes', f['additionalNotesIds']) || [];
          f.additionalNotes = additionalNotes.filter((item: any) => item.typeOfNote === 'Report Note');
          f.additionalNotes = this.utils.modifyNotes(f.additionalNotes);
        }
        if (f?.additionalNotes) {
          f.additionalNotes = f.additionalNotes.filter((n: any) => {
            if (n.typeOfNote === 'Report Note') {
              f.isVisible = true;
              return true;
            }
            return false;
          });
          for (let n of f.additionalNotes) {
            n.img = await this.getImage(n);
          }
        }

        if (f.comments?.length) {
          f.comments = f.comments.filter((item: any) => item.isCompleted);
        } else {
          f.comments = [];
        }
      }

      flow = this.utils.sort(flow, 'templateCategoryOrder', 1);
      let lastCategory = '';
      for (let f of flow) {
        if (f.isVisible) {
          f.isCategoryVisible = f['templateCategoryOrder'] !== lastCategory;
          lastCategory = f['templateCategoryOrder'];
        }
      }

      flow = flow.filter((item: any) => {
        return item.isVisible;
      });

      this.reportGroups[0] = {title: 'Flow', items: flow || []};
      this.reportGroups[1] = {title: 'Pictures and Notes', items: picturesAndNotes || []};
      this.reportGroups[2] = {title: 'Trainings', items: trainings || []};
      this.reportGroups[3] = {title: 'Services', items: services || []};
      this.reportGroups[4] = {title: 'Education Modules', items: educationModules || []};
      this.reportGroups[5] = {title: 'Signature', items: signature ? [signature] : []};
      let isDataExist = false;
      for (let group of this.reportGroups) {
        if (group.items.length) {
          isDataExist = true;
          break;
        }
      }
      clearTimeout(this.dataExistTimeout);
      this.dataExistTimeout = setTimeout(() => {
        this.isDataExist = isDataExist;
      }, 1000);
      this.isShowImageShareButton = Boolean(this.storage.getItem('isShowImageShareButton'));
      this.reportStatus = true;
      this.onStatusChange();
    } else {
      this.reportStatus = false;
      this.onStatusChange();
      clearTimeout(this.dataExistTimeout);
      this.dataExistTimeout = setTimeout(() => {
        this.isDataExist = false;
      }, 3000);
    }
  }

  async share(note: any) {
    if (note.filename) {
      await this.utils.shareLocalFile(note.filename);
    } else {
      alert("No local file found");
    }
  }

  onFollowUpStatusChange(value: boolean) {
    this.followUpStatus = value;
    this.onStatusChange();
  }
}
